import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from "@/components/ui/table";
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationPrevious, PaginationNext, PaginationEllipsis, PaginationPageSize } from "@/components/ui/pagination";
import { Progress } from "@/components/ui/progress";
import { IndianRupee, TrendingUp, Users } from "lucide-react";
import { formatCurrency } from "@/lib/utils";
import { toast } from "sonner";
import { getEmployees } from "@/services/adminService";
import { useSubscription } from "@/contexts/SubscriptionContext";

const SalaryStructure = () => {
  const { hasFeature } = useSubscription();
  // Departments are a paid feature — hidden, never blanked.
  const canUseDepartments = hasFeature("department_mapping");

  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const handlePageSizeChange = (value) => {
    setPageSize(Number(value));
    setPage(1); // reset to first page when page size changes
  };

  const totalItems = employees.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));


  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const filteredemployees = React.useMemo(() => {
    const start = (page - 1) * pageSize;
    return employees.slice(start, start + pageSize);
  }, [employees, page, pageSize]);

  const buildPages = (current, total) => {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

    const pages = new Set([1, total, current, current - 1, current + 1]);
    const cleaned = [...pages].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);

    const result = [];
    for (let i = 0; i < cleaned.length; i++) {
      result.push(cleaned[i]);
      if (i < cleaned.length - 1 && cleaned[i + 1] - cleaned[i] > 1) {
        result.push("...");
      }
    }
    return result;
  };

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await getEmployees();
      const activeEmployees = (response || []).filter(
        (emp) => emp.status === "active"
      );
      setEmployees(activeEmployees);
    } catch (error) {
      console.error("Error fetching employees:", error);
      toast.error("Failed to load employees");
    } finally {
      setLoading(false);
    }
  };

  // Get totals for one employee from dynamic salary_structure[]
  const getEmployeeTotals = (employee) => {
    const components = employee.salary_structure || [];

    const earningsTotal = components
      .filter((c) => c.category === "earnings")
      .reduce((sum, c) => sum + Number(c.amount || 0), 0);

    const deductionsTotal = components
      .filter((c) => c.category === "deductions")
      .reduce((sum, c) => sum + Number(c.amount || 0), 0);

    // Prefer backend net_salary if present, else derive
    const net =
      employee.net_salary !== undefined && employee.net_salary !== null
        ? Number(employee.net_salary)
        : earningsTotal - deductionsTotal;

    return { earningsTotal, deductionsTotal, net };
  };

  const getSalaryInsights = () => {
    if (!employees.length) {
      return { avgSalary: 0, totalPayroll: 0, salaryRanges: [] };
    }

    const nets = employees.map((emp) => getEmployeeTotals(emp).net);

    const totalPayroll = nets.reduce((sum, v) => sum + v, 0);
    const avgSalary = totalPayroll / nets.length;

    const ranges = [
      { label: "Below 50k", count: nets.filter((s) => s < 50000).length },
      {
        label: "50k - 75k",
        count: nets.filter((s) => s >= 50000 && s < 75000).length,
      },
      {
        label: "75k - 1L",
        count: nets.filter((s) => s >= 75000 && s < 100000).length,
      },
      { label: "Above 1L", count: nets.filter((s) => s >= 100000).length },
    ];

    return { avgSalary, totalPayroll, salaryRanges: ranges };
  };

  const insights = getSalaryInsights();

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid gap-4 md:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="p-6 space-y-3">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-8 w-16" />
                <Skeleton className="h-3 w-20" />
              </CardContent>
            </Card>
          ))}
        </div>
        <Card>
          <CardContent className="p-6 space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-14 w-full" />
            ))}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="salary-structure">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
            Salary Structure
          </h1>
          <p className="text-gray-500 dark:text-gray-400">
            View salary components for all active employees
          </p>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-2">
              <Users className="h-8 w-8 text-blue-600" />
              <div>
                <p className="text-2xl font-bold">{employees.length}</p>
                <p className="text-xs text-gray-500">Active Employees</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-2">
              <IndianRupee className="h-8 w-8 text-primary" />
              <div>
                <p className="text-2xl font-bold">
                  {formatCurrency(insights.avgSalary)}
                </p>
                <p className="text-xs text-gray-500">Average Net Salary</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-2">
              <TrendingUp className="h-8 w-8 text-purple-600" />
              <div>
                <p className="text-2xl font-bold">
                  {formatCurrency(insights.totalPayroll)}
                </p>
                <p className="text-xs text-gray-500">Total Monthly Payroll</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Salary Distribution */}
      <Card>
        <CardHeader>
          <CardTitle>Salary Distribution (by Net Salary)</CardTitle>
        </CardHeader>
        <CardContent>
          {insights.salaryRanges.map((range, index) => (
            <div key={index} className="flex items-center space-x-4 mb-3">
              <div className="w-28 text-sm text-gray-700 dark:text-gray-300">
                {range.label}
              </div>
              <div className="flex-1">
                <Progress
                  value={
                    employees.length
                      ? (range.count / employees.length) * 100
                      : 0
                  }
                  className="h-2"
                />
              </div>
              <div className="w-8 text-sm text-gray-600 dark:text-gray-400 text-right">
                {range.count}
              </div>
            </div>
          ))}
          {!employees.length && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No employees to display.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Employee Salary List */}
      <Card>
        <CardHeader>
          <CardTitle>Employee Salary Structures</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-b border-gray-200 dark:border-gray-700">
                  <TableHead className="text-gray-700 dark:text-gray-300">
                    Employee
                  </TableHead>
                  <TableHead className="text-gray-700 dark:text-gray-300">
                    Department
                  </TableHead>
                  <TableHead className="text-gray-700 dark:text-gray-300">
                    Total Earnings
                  </TableHead>
                  <TableHead className="text-gray-700 dark:text-gray-300">
                    Total Deductions
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {employees.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      className="py-10 text-center text-gray-500 dark:text-gray-400"
                    >
                      No active employees found.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredemployees.map((employee) => {
                    const { earningsTotal, deductionsTotal } =
                      getEmployeeTotals(employee);

                    return (
                      <TableRow
                        key={employee._id}
                        className="border-b border-gray-200 dark:border-gray-700"
                      >
                        <TableCell>
                          <div className="flex flex-col">
                            <span className="font-medium text-gray-900 dark:text-gray-200">
                              {employee.name}
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {employee.employee_id}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-gray-800 dark:text-gray-300">
                          <div className="flex flex-col">
                            {canUseDepartments && (
                              <span>{employee.department}</span>
                            )}
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {employee.designation}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-gray-800 dark:text-gray-300">
                          {formatCurrency(earningsTotal)}
                        </TableCell>
                        <TableCell className="text-red-600 dark:text-red-400">
                          {formatCurrency(deductionsTotal)}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
      {employees.length > 0 && (
        <div className="flex items-center justify-end sm:justify-between gap-3 mt-4">
          <PaginationPageSize value={pageSize} onValueChange={handlePageSizeChange} />
          <Pagination className="mx-0 justify-end">
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  aria-disabled={page === 1}
                  className={page === 1 ? "pointer-events-none opacity-50" : ""}
                  onClick={(e) => {
                    e.preventDefault();
                    setPage((p) => Math.max(1, p - 1));
                  }}
                />
              </PaginationItem>

              {buildPages(page, totalPages).map((p, idx) => {
                if (p === "...") {
                  return (
                    <PaginationItem key={`e-${idx}`}>
                      <PaginationEllipsis />
                    </PaginationItem>
                  );
                }

                return (
                  <PaginationItem key={p}>
                    <PaginationLink
                      href="#"
                      isActive={p === page}
                      onClick={(e) => {
                        e.preventDefault();
                        setPage(p);
                      }}
                    >
                      {p}
                    </PaginationLink>
                  </PaginationItem>
                );
              })}

              <PaginationItem>
                <PaginationNext
                  href="#"
                  aria-disabled={page === totalPages}
                  className={page === totalPages ? "pointer-events-none opacity-50" : ""}
                  onClick={(e) => {
                    e.preventDefault();
                    setPage((p) => Math.min(totalPages, p + 1));
                  }}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </div>
  );
};

export default SalaryStructure;
